import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Group,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root/page-content-root';
import { DOCUMENT_PICKER_LIST } from '#graphql/queries/document-picker-list';
import { useAppSettings } from '#providers/app-settings-provider';
import { documentViewHref } from '#utils/document-app-path';
import {
  DOCUMENT_PATH_DESCRIPTION,
  isValidDocumentPath,
} from '#utils/document-path';
import { formatDocumentName } from '#utils/format-document-name';

export function DocumentsEmptyState() {
  const navigate = useNavigate();
  const [createPath, setCreatePath] = useState('');
  const { documentPathRegex } = useAppSettings();

  const trimmedCreatePath = createPath.trim();
  const isCreatePathValid = trimmedCreatePath
    ? isValidDocumentPath(trimmedCreatePath, documentPathRegex)
    : false;

  const handleCreateSubmit = () => {
    const path = createPath.trim();
    if (!path || !isValidDocumentPath(path, documentPathRegex)) return;

    setCreatePath('');
    navigate(documentViewHref(path));
  };

  const { data: documentsData, loading: documentsLoading } = useQuery(
    DOCUMENT_PICKER_LIST,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const documents = documentsData?.documents ?? [];

  return (
    <PageContentRoot>
      <Box maxW="2xl" mx="auto" width="100%">
        <VStack gap={8} width="100%" align="stretch">
          <VStack gap={4} width="100%" align="stretch">
            <Text fontSize="lg" color="fg" fontWeight="medium">
              Create document
            </Text>
            <Text fontSize="sm" color="fg.muted">
              Enter a path to create a new document. You'll be taken to the
              document page where you can create it.
            </Text>
            <VStack gap={2} align="stretch">
              <Group
                attached
                width="100%"
                minW={0}
                display="grid"
                gridTemplateColumns="1fr auto"
                alignItems="stretch"
              >
                <Input
                  placeholder="e.g. folder/doc-name or path (with parens)"
                  value={createPath}
                  onChange={(e) => setCreatePath(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateSubmit();
                  }}
                  aria-invalid={!!trimmedCreatePath && !isCreatePathValid}
                  borderColor={
                    trimmedCreatePath && !isCreatePathValid
                      ? 'red.500'
                      : undefined
                  }
                  minW={0}
                />
                <Button
                  colorScheme="blue"
                  onClick={handleCreateSubmit}
                  disabled={!trimmedCreatePath || !isCreatePathValid}
                  style={{
                    height: '100%',
                    minHeight: '100%',
                    alignSelf: 'stretch',
                  }}
                >
                  Create
                </Button>
              </Group>
              {trimmedCreatePath && !isCreatePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
            </VStack>
          </VStack>
          <VStack gap={2} align="stretch" width="100%">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="fg.muted"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Your documents
            </Text>
            {documentsLoading ? (
              <HStack py={2} gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="fg.muted">
                  Loading…
                </Text>
              </HStack>
            ) : documents.length === 0 ? (
              <Text fontSize="sm" color="fg.muted">
                No documents yet.
              </Text>
            ) : (
              <>
                {documents.map((doc) => {
                  const docHref = documentViewHref(doc.path);
                  const label = formatDocumentName(doc.path).join(' > ');
                  return (
                    <Link
                      key={doc.id}
                      to={docHref}
                      style={{ textDecoration: 'none' }}
                    >
                      <HStack
                        gap={3}
                        py={2}
                        px={1}
                        borderRadius="md"
                        _hover={{ bg: 'bg.muted' }}
                        _active={{ bg: 'bg.emphasized' }}
                      >
                        <Text
                          fontWeight="normal"
                          color="fg"
                          fontSize="sm"
                          lineClamp={1}
                        >
                          {label}
                        </Text>
                      </HStack>
                    </Link>
                  );
                })}
              </>
            )}
          </VStack>
        </VStack>
      </Box>
    </PageContentRoot>
  );
}
